import {
  ensureLocalhostJwtSession,
  isLocalhostRuntime,
  verifyBackendSessionToken,
} from '@/lib/ghAuth';
import { startWebAuthBootstrap } from '@/lib/webAuthBootstrap';
import { IS_NATIVE, onNativeMessage, postToNative } from '@/lib/nativeBridge';
import { clearUserSessionData } from '@/lib/userSession';
import { useAuthStore } from '@/stores/AuthStore';
import { useEffect, useRef, useState } from 'react';
import { AppBootstrapSplash } from '@/components/AppBootstrapSplash';

// How long to wait for native to deliver a token after app.ready before unblocking as anonymous.
const NATIVE_AUTH_TIMEOUT_MS = 3000;

type BootstrapView = {
  state: 'loading' | 'error';
  message: string;
};

export function GhAuthProvider({ children }: { children: React.ReactNode }) {
  const beginSessionResolution = useAuthStore(s => s.beginSessionResolution);
  const clearAuth = useAuthStore(s => s.clearAuth);
  const hasHydrated = useAuthStore(s => s.hasHydrated);
  const sessionState = useAuthStore(s => s.sessionState);
  const setReady = useAuthStore(s => s.setReady); // compatibility with existing app gates
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [bootstrapView, setBootstrapView] = useState<BootstrapView>({
    state: 'loading',
    message: 'Securing your session before the team experience loads.',
  });
  const retryWebBootstrap = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (!hasHydrated) {
      return;
    }

    beginSessionResolution();

    if (IS_NATIVE) {
      let settled = false;
      let nativeAuthRevision = 0;

      const settle = () => {
        if (settled) return;
        settled = true;
        setIsBootstrapping(false);
      };

      const unsubToken = onNativeMessage('auth.tokenDelivery', payload => {
        const token = payload.token as string | undefined;
        if (token) {
          const revision = ++nativeAuthRevision;
          const previousUserId = useAuthStore.getState().userId;
          beginSessionResolution();

          void verifyBackendSessionToken(token).then(result => {
            if (revision !== nativeAuthRevision) return;

            if (result.kind === 'authenticated') {
              if (previousUserId && previousUserId !== result.userId) {
                clearUserSessionData();
              }
              useAuthStore.getState().resolveAuthenticatedSession({
                token,
                userId: result.userId,
              });
            } else {
              clearUserSessionData();
              clearAuth();
            }
            settle();
          });
        } else {
          nativeAuthRevision += 1;
          clearUserSessionData();
          clearAuth();
          settle();
        }
      });

      const unsubLogout = onNativeMessage('auth.userLoggedOut', () => {
        nativeAuthRevision += 1;
        clearUserSessionData();
        clearAuth();
        settle();
      });

      // Signal native that the web is ready to receive auth.
      postToNative('app.ready');

      // Unblock after timeout in case native never responds (e.g. anonymous game).
      const timer = setTimeout(() => {
        if (!settled) {
          nativeAuthRevision += 1;
          postToNative('auth.requestLogin');
          clearUserSessionData();
          clearAuth();
          settle();
        }
      }, NATIVE_AUTH_TIMEOUT_MS);

      return () => {
        nativeAuthRevision += 1;
        unsubToken();
        unsubLogout();
        clearTimeout(timer);
      };
    }

    // On localhost, preserve persisted auth state and do not overwrite it from GH snapshot on reload.
    if (isLocalhostRuntime()) {
      void ensureLocalhostJwtSession().finally(() => {
        const syncedUserId = useAuthStore.getState().userId ?? undefined;
        setReady(syncedUserId);
        setIsBootstrapping(false);
      });
      return;
    }

    const controller = startWebAuthBootstrap({
      onLoading: message => setBootstrapView({ state: 'loading', message }),
      onError: message => setBootstrapView({ state: 'error', message }),
      onResolved: () => setIsBootstrapping(false),
    });
    retryWebBootstrap.current = controller.retry;

    return () => {
      controller.dispose();
      if (retryWebBootstrap.current === controller.retry) {
        retryWebBootstrap.current = null;
      }
    };
  }, [beginSessionResolution, clearAuth, hasHydrated, setReady]);

  if (!hasHydrated || isBootstrapping || sessionState === 'resolving') {
    return (
      <AppBootstrapSplash
        state={bootstrapView.state}
        message={bootstrapView.message}
        actionLabel={bootstrapView.state === 'error' ? 'Try again' : undefined}
        onAction={bootstrapView.state === 'error' ? () => retryWebBootstrap.current?.() : undefined}
      />
    );
  }

  return <>{children}</>;
}
